import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { notesAPI } from '../../api/notesApi';
import { useAuth } from '../../contexts/AuthContext';
import useIsMobile from '../../hooks/useIsMobile';
import { useNotesActions } from './NotesActionsContext';
import { isNotesAdmin } from '../../constants/notes';
import {
    PlusIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    DocumentTextIcon,
    ArrowDownTrayIcon,
    TrashIcon,
    PencilIcon,
    HomeIcon,
    MagnifyingGlassIcon,
    UserGroupIcon,
    LockClosedIcon,
    XMarkIcon,
    FaceSmileIcon,
    UserPlusIcon,
    BriefcaseIcon
} from '@heroicons/react/24/outline';

function groupPrivateNotesByOwner(notes) {
    const m = new Map();
    for (const n of notes) {
        const oid = n.owner;
        const label = n.owner_details?.full_name || n.owner_details?.email || `User ${oid}`;
        if (!m.has(oid)) m.set(oid, { ownerId: oid, label, notes: [] });
        m.get(oid).notes.push(n);
    }
    return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label));
}

// Recursive Tree Node Component
// Recursive Tree Node Component
const TreeNode = ({ note, level = 0, onSelect, activeId, onExpand, onDelete }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [children, setChildren] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [currentTitle, setCurrentTitle] = useState(note.title);
    const navigate = useNavigate();

    // Note prop değişirse title'ı güncelle
    useEffect(() => {
        setCurrentTitle(note.title);
    }, [note.title]);

    // Load children when expanded
    const handleToggle = async (e) => {
        e.stopPropagation();

        if (!isOpen && note.has_children && children.length === 0) {
            setIsLoading(true);
            try {
                const response = await notesAPI.getNotes(note.id);
                const data = Array.isArray(response) ? response : (response?.results || []);
                setChildren(data);
            } catch (error) {
                console.error('Failed to load children', error);
            } finally {
                setIsLoading(false);
            }
        }

        setIsOpen(!isOpen);
        if (onExpand) onExpand(note.id, !isOpen);
    };

    const handleSelect = () => {
        if (!isRenaming) {
            onSelect(note);
            navigate(`/notes/${note.id}`);
        }
    };

    const handleAddChild = async (e) => {
        e.stopPropagation();
        try {
            const newNote = await notesAPI.createNote({
                title: 'Untitled',
                parent: note.id
            });

            if (!isOpen) setIsOpen(true);
            setChildren([...children, newNote]);
            navigate(`/notes/${newNote.id}`);
        } catch (error) {
            console.error('Failed to create sub-note', error);
        }
    };

    const handleDeleteNote = async (e) => {
        e.stopPropagation();
        if (window.confirm('Bu notu silmek istediğinizden emin misiniz?')) {
            try {
                await notesAPI.deleteNote(note.id);

                // localStorage'daki recent_notes listesinden sil
                try {
                    const recents = JSON.parse(localStorage.getItem('recent_notes') || '[]');
                    const filtered = recents.filter(r => r.id !== note.id);
                    localStorage.setItem('recent_notes', JSON.stringify(filtered));
                } catch (e) {
                    console.error('Failed to update recents', e);
                }

                // Önce callback'i çağır (parent listesinden çıkar)
                if (onDelete) {
                    onDelete(note.id);
                }
                // Eğer silinen not şu an aktif olan not ise, ana sayfaya yönlendir
                if (activeId === note.id) {
                    navigate('/notes');
                }
            } catch (error) {
                console.error('Failed to delete note', error);
                alert('Not silinemedi: ' + (error.message || 'Bilinmeyen hata'));
            }
        }
    };

    const handleRenameStart = (e) => {
        e.stopPropagation();
        setIsRenaming(true);
    };

    const handleRenameSubmit = async (e) => {
        // Form submit veya blur durumunda
        if (e.type === 'keydown' && e.key !== 'Enter') return;
        e.stopPropagation(); // Click event propagation'ı engelle

        setIsRenaming(false);
        if (!currentTitle.trim()) {
            setCurrentTitle(note.title);
            return;
        }
        if (currentTitle.trim() === note.title) return; // Değişiklik yoksa çık

        try {
            await notesAPI.updateNote(note.id, { title: currentTitle });
            // Başarılı olursa zaten currentTitle güncel, ek bir şey yapmaya gerek yok
        } catch (error) {
            console.error('Failed to rename note', error);
            setCurrentTitle(note.title); // Hata varsa geri al
        }
    };

    const handleChildDelete = (childId) => {
        setChildren(children.filter(c => c.id !== childId));
    };

    return (
        <div className="select-none">
            <div
                className={`group flex items-center py-1 px-2 pr-3 text-sm rounded-md cursor-pointer transition-colors ${activeId === note.id
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`}
                style={{ paddingLeft: `${level * 12 + 8}px` }}
                onClick={handleSelect}
            >
                {/* Expand Icon */}
                <div
                    onClick={handleToggle}
                    className={`p-0.5 rounded-sm hover:bg-gray-200 dark:hover:bg-gray-700 mr-1 text-gray-400 dark:text-gray-500 ${!note.has_children && children.length === 0 ? 'invisible' : ''}`}
                >
                    {isOpen ? (
                        <ChevronDownIcon className="w-3 h-3" />
                    ) : (
                        <ChevronRightIcon className="w-3 h-3" />
                    )}
                </div>

                {/* Note Icon */}
                <span className="mr-2 text-lg leading-none">
                    {note.icon || '📄'}
                </span>

                {/* Title (Renaming Logic) */}
                {isRenaming ? (
                    <input
                        autoFocus
                        type="text"
                        className="flex-1 bg-white dark:bg-gray-800 border border-blue-500 rounded px-1 py-0.5 text-sm outline-none w-full mr-2"
                        value={currentTitle}
                        onChange={(e) => setCurrentTitle(e.target.value)}
                        onBlur={handleRenameSubmit}
                        onKeyDown={handleRenameSubmit}
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    <span className="flex-1 truncate">{currentTitle || 'Untitled'}</span>
                )}

                {/* Actions (Visible on Hover) */}
                {!isRenaming && (
                    <div className="hidden group-hover:flex items-center space-x-1">
                        <button
                            onClick={handleRenameStart}
                            className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                            title="Rename"
                        >
                            <PencilIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={handleAddChild}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            title="Add sub-page"
                        >
                            <PlusIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={handleDeleteNote}
                            className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            title="Delete page"
                        >
                            <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
            </div>

            {/* Children */}
            {isOpen && (
                <div className="border-l border-gray-200 dark:border-gray-800 ml-[15px]">
                    {isLoading && <div className="pl-6 py-1 text-xs text-gray-400">Loading...</div>}
                    {children.map(child => (
                        <TreeNode
                            key={child.id}
                            note={child}
                            level={level + 1}
                            onSelect={onSelect}
                            activeId={activeId}
                            onDelete={handleChildDelete}
                        />
                    ))}
                    {children.length === 0 && !isLoading && (
                        <div className="pl-6 py-1 text-xs text-gray-400 italic">No pages inside</div>
                    )}
                </div>
            )}
        </div>
    );
};

const NotesSidebar = ({ onClose }) => {
    const [privateNotes, setPrivateNotes] = useState([]);
    const [sharedNotes, setSharedNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isMobile = useIsMobile();
    const { actionsState } = useNotesActions();
    const mountedRef = useRef(false);

    useEffect(() => {
        if (!mountedRef.current) {
            mountedRef.current = true;
            return;
        }

        if (isMobile && onClose) {
            onClose();
        }
    }, [location.pathname, onClose, isMobile]);

    const handleSearch = async (e) => {
        if (e.key === 'Enter') {
            setIsLoading(true);
            try {
                if (!searchQuery.trim()) {
                    await loadAllNotes();
                } else {
                    const response = await notesAPI.getNotes(null, searchQuery);
                    const data = Array.isArray(response) ? response : (response?.results || []);
                    if (isNotesAdmin(user)) {
                        setPrivateNotes(data);
                        setSharedNotes([]);
                    } else {
                        setPrivateNotes(data.filter(n => n.owner === user.id));
                        setSharedNotes(data.filter(n => n.owner !== user.id));
                    }
                }
            } catch (error) {
                console.error('Search failed', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Extract active ID from URL path (e.g. /notes/123 -> 123)
    const activeId = location.pathname.split('/notes/')[1]
        ? parseInt(location.pathname.split('/notes/')[1])
        : null;

    const privateByOwner = useMemo(() => {
        if (!isNotesAdmin(user)) return null;
        return groupPrivateNotesByOwner(privateNotes);
    }, [privateNotes, user]);

    useEffect(() => {
        loadAllNotes();
    }, []);

    const loadAllNotes = async () => {
        setIsLoading(true);
        try {
            const [pResponse, sResponse] = await Promise.all([
                notesAPI.getNotes(null, null, 'private'),
                notesAPI.getNotes(null, null, 'shared')
            ]);

            const pData = Array.isArray(pResponse) ? pResponse : (pResponse?.results || []);
            const sData = Array.isArray(sResponse) ? sResponse : (sResponse?.results || []);

            setPrivateNotes(pData);
            setSharedNotes(sData);
        } catch (error) {
            console.error('Failed to load notes', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNoteDelete = (deletedId, type) => {
        if (type === 'private') {
            setPrivateNotes(privateNotes.filter(n => n.id !== deletedId));
        } else {
            setSharedNotes(sharedNotes.filter(n => n.id !== deletedId));
        }
    };

    const handleCreateRootNote = async () => {
        try {
            const newNote = await notesAPI.createNote({
                title: 'Untitled',
                parent: null
            });
            setPrivateNotes([...privateNotes, newNote]);
            navigate(`/notes/${newNote.id}`);
        } catch (error) {
            console.error('Failed to create root note', error);
        }
    };

    return (
        <div className="flex flex-col h-full bg-gray-50/50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800">

            {/* Sidebar Header */}
            <div className="px-4 py-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-800">
                <div
                    className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => navigate('/notes')}
                >
                    <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center text-white text-xs font-bold">
                        N
                    </div>
                    <span className="font-semibold text-sm text-gray-700 dark:text-gray-200">
                        Team Notes
                    </span>
                </div>

                {/* Back to Modules */}
                <button
                    onClick={() => navigate('/module-selection')}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs flex items-center"
                    title="Modül Seçimine Dön"
                >
                    Exit
                </button>
            </div>

            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-800 md:hidden">
                <button
                    onClick={onClose}
                    className="w-full inline-flex items-center justify-center rounded-md bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200"
                >
                    <XMarkIcon className="w-4 h-4 mr-1.5" />
                    Menüyü Kapat
                </button>
            </div>

            {/* Quick Links / Search */}
            <div className="px-3 py-2 space-y-1">
                {showSearch ? (
                    <div className="w-full px-2 py-1.5 flex items-center bg-white dark:bg-gray-800 border border-blue-500 rounded-md shadow-sm">
                        <MagnifyingGlassIcon className="w-4 h-4 text-blue-500 mr-2" />
                        <input
                            autoFocus
                            type="text"
                            placeholder="Search notes..."
                            className="w-full bg-transparent border-none outline-none text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearch}
                            onBlur={() => {
                                if (!searchQuery) {
                                    setShowSearch(false);
                                    loadAllNotes();
                                }
                            }}
                        />
                    </div>
                ) : (
                    <button
                        onClick={() => setShowSearch(true)}
                        className="w-full flex items-center px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-md"
                    >
                        <MagnifyingGlassIcon className="w-4 h-4 mr-2" />
                        <span>Search</span>
                    </button>
                )}
                <button
                    onClick={() => navigate('/notes')}
                    className="w-full flex items-center px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-md"
                >
                    <HomeIcon className="w-4 h-4 mr-2" />
                    <span>Home</span>
                </button>
            </div>

            {actionsState.hasActiveNote && (
                <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-800 space-y-1">
                    <div className="mb-2 px-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                        Not İşlemleri
                    </div>

                    <button
                        onClick={actionsState.onIconEdit}
                        className="w-full flex items-center px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-md"
                    >
                        <FaceSmileIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                        İkon Ekle
                    </button>

                    <button
                        onClick={actionsState.onWordImport}
                        disabled={actionsState.importingWord}
                        className="w-full flex items-center px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-md disabled:opacity-50"
                    >
                        <DocumentTextIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                        {actionsState.importingWord ? 'Word Aktarılıyor...' : 'Word’den Aktar'}
                    </button>

                    <button
                        onClick={actionsState.onWordExport}
                        disabled={actionsState.exportingWord}
                        className="w-full flex items-center px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-md disabled:opacity-50"
                    >
                        <ArrowDownTrayIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                        {actionsState.exportingWord ? 'İndiriliyor...' : "Word'e Aktar"}
                    </button>

                    {actionsState.isOwner && (
                        <button
                            onClick={actionsState.onShare}
                            className="w-full flex items-center px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-md"
                        >
                            <UserPlusIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                            Paylaş ({actionsState.shareCount || 0})
                        </button>
                    )}

                    <button
                        onClick={actionsState.onCreateTicket}
                        className="w-full flex items-center px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-md"
                    >
                        <BriefcaseIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                        Talep Oluştur
                    </button>
                </div>
            )}

            {/* Root Notes List */}
            <div className="flex-1 overflow-y-auto px-2 py-2">
                {/* Private Section */}
                <div className="mb-2 px-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center">
                    <LockClosedIcon className="w-3 h-3 mr-1.5" />
                    Private
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-4">
                        <div className="animate-spin h-3 w-3 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                    </div>
                ) : (
                    <div className="space-y-0.5 mb-6">
                        {privateByOwner && privateByOwner.length > 0 ? (
                            privateByOwner.map((group) => (
                                <div key={group.ownerId} className="mb-3">
                                    <div className="px-2 py-1 text-[10px] font-semibold text-gray-500 dark:text-gray-400 truncate">
                                        {group.label}
                                    </div>
                                    <div className="space-y-0.5">
                                        {group.notes.map(note => (
                                            <TreeNode
                                                key={note.id}
                                                note={note}
                                                activeId={activeId}
                                                onSelect={() => { }}
                                                onDelete={(id) => handleNoteDelete(id, 'private')}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : (
                            privateNotes.map(note => (
                                <TreeNode
                                    key={note.id}
                                    note={note}
                                    activeId={activeId}
                                    onSelect={() => { }}
                                    onDelete={(id) => handleNoteDelete(id, 'private')}
                                />
                            ))
                        )}
                        {privateNotes.length === 0 && !isLoading && (
                            <div className="px-2 py-2 text-xs text-gray-400 italic">No personal notes</div>
                        )}

                        <button
                            onClick={handleCreateRootNote}
                            className="w-full mt-1 flex items-center px-2 py-1.5 text-xs font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/10 rounded-md transition-all"
                        >
                            <PlusIcon className="w-3.5 h-3.5 mr-2" />
                            New Page
                        </button>
                    </div>
                )}

                {/* Shared Section */}
                <div className="mb-2 px-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center">
                    <UserGroupIcon className="w-3.5 h-3.5 mr-1.5" />
                    Shared with me
                </div>

                {!isLoading && (
                    <div className="space-y-0.5">
                        {sharedNotes.map(note => (
                            <TreeNode
                                key={note.id}
                                note={note}
                                activeId={activeId}
                                onSelect={() => { }}
                                onDelete={(id) => handleNoteDelete(id, 'shared')}
                            />
                        ))}
                        {sharedNotes.length === 0 && !isLoading && (
                            <div className="px-2 py-2 text-xs text-gray-400 italic">No shared notes</div>
                        )}
                    </div>
                )}
            </div>

            {/* Sidebar Footer */}
            <div className="p-3 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center space-x-2 text-xs text-gray-400">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                    <span>Online</span>
                </div>
            </div>
        </div>
    );
};

export default NotesSidebar;
